import type { OnDestroy, Type } from '@angular/core';
import { ComponentFactoryResolver, Injectable, ViewContainerRef } from '@angular/core';

import type { Observable } from 'rxjs';
import { merge, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';

import type { CreateNodeView, RtPMPlugin } from '@core/prosemirror-legacy';

export interface ProsemirrorAngularComponent {
  pmOnDestroy$: Subject<void>;
  getNodeView: CreateNodeView;
}

@Injectable()
export class ProsemirrorBaseConfig implements OnDestroy {
  public eventStream$: Subject<any>;
  protected ngOnDestroy$ = new Subject<void>();

  constructor(
    protected cfr: ComponentFactoryResolver,
    protected vcRef: ViewContainerRef,
  ) {}

  public listenForPluginEvents(plugins: RtPMPlugin[]) {
    const streams = <Array<Observable<any>>>(
      plugins.map(({ eventStream$ }) => eventStream$).filter(Boolean)
    );

    merge(...streams)
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(event => this.eventStream$.next(event));
  }

  public ngOnDestroy() {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  protected prosemirrorComponentFactory(component: Type<ProsemirrorAngularComponent>) {
    return (...args: Parameters<CreateNodeView>) => {
      const factory = this.cfr.resolveComponentFactory(component);
      const componentRef = factory.create(this.vcRef.injector);
      componentRef.changeDetectorRef.detectChanges();

      componentRef.instance.pmOnDestroy$.pipe(take(1)).subscribe(() => componentRef.destroy());
      return componentRef.instance.getNodeView(...args);
    };
  }
}
